import { useState, useEffect } from "react";
import type { RouteObject } from "react-router-dom";
import { Navigate } from "react-router-dom";
import NotFound from "../pages/NotFound";
import Home from "../pages/home/page";
import CategoryPage from "../pages/category/page";
import ListingDetail from "../pages/listing-detail/page";
import SearchPage from "../pages/search/page";
import { useLoginTemplate, useRegisterTemplate, useVipTemplate } from "../templates/index";
import ForgotPasswordPage from "../pages/forgot-password/page";
import PublishPage from "../pages/publish/page";
import DashboardPage from "../pages/dashboard/page";
import ProfilePage from "../pages/dashboard/profile/page";
import MyListingsPage from "../pages/dashboard/my-listings/page";
import FavoritesPage from "../pages/dashboard/favorites/page";
import HistoryPage from "../pages/dashboard/history/page";
import PaymentRecordsPage from "../pages/dashboard/payment-records/page";
import EditListingPage from "../pages/dashboard/edit-listing/page";
import RequireAuth from "../components/feature/RequireAuth";
import RequireAdmin from "../components/feature/RequireAdmin";
import AdminLoginPage from "../pages/admin/login/page";
import AdminDashboard from "../pages/admin/page";
import AdminListingsPage from "../pages/admin/listings/page";
import AdminUsersPage from "../pages/admin/users/page";
import AdminReportsPage from "../pages/admin/reports/page";
import AdminVipOrdersPage from "../pages/admin/vip-orders/page";
import AdminSettingsPage from "../pages/admin/settings/page";
import AdminCategoriesPage from "../pages/admin/categories/page";
import AdminPaymentPage from "../pages/admin/payment/page";
import AdminPublishPage from "../pages/admin/publish/page";
import AdminEditListingPage from "../pages/admin/edit-listing/page";
import AdminNavMenusPage from "../pages/admin/nav-menus/page";
import AdminSinglePagesPage from "../pages/admin/single-pages/page";
import AdminPinManagementPage from "../pages/admin/pin-management/page";
import AdminDecorationPage from "../pages/admin/decoration/page";
import AdminCloudDataPage from "../pages/admin/cloud-data/page";
import AdminMySitesPage from "../pages/admin/my-sites/page";
import SinglePageView from "../pages/single-page/page";
import TenantIndexPage from "../pages/tenant/page";
import TenantSitesPage from "../pages/tenant/sites/page";
import TenantAccountPage from "../pages/tenant/account/page";
import TenantDashboardPage from "../pages/tenant/dashboard/page";
import TenantUsersPage from "../pages/tenant/users/page";
import TenantPlansPage from "../pages/tenant/plans/page";
import PlatformIndexPage from "../pages/platform/page";
import PlatformDashboardPage from "../pages/platform/dashboard/page";
import PlatformAgentsPage from "../pages/platform/agents/page";
import PlatformPlansPage from "../pages/platform/plans/page";
import PlatformSitesPage from "../pages/platform/sites/page";
import PlatformSettingsPage from "../pages/platform/settings/page";
import { isMainDomain } from "../config/domain";

/** 子域名站点守卫：检查站点状态，非活跃站点显示关闭提示 */
function SubdomainSiteGuard() {
  const [state, setState] = useState<'loading' | 'active' | 'closed' | 'unknown'>('loading');
  const [siteName, setSiteName] = useState('');

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const domain = window.location.hostname;
    if (!domain || isMainDomain()) {
      setState('active');
      return;
    }
    
    fetch(`/api/sites/by-domain?domain=${encodeURIComponent(domain)}`)
      .then(r => r.json().catch(() => null))
      .then(data => {
        if (data && data.code === 0 && data.data) {
          setSiteName(data.data.name || domain);
          setState('active');
        } else if (data && data.code === 0 && data.data === null) {
          // 站点存在但非活跃状态（已停止或已到期）
          setState('closed');
        } else {
          // 域名未匹配到任何站点
          setState('unknown');
        }
      })
      .catch(() => setState('unknown'));
  }, []);

  if (state === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="flex flex-col items-center gap-3">
          <i className="ri-loader-4-line animate-spin text-3xl text-gray-400" />
          <p className="text-sm text-gray-500">加载中...</p>
        </div>
      </div>
    );
  }

  if (state === 'closed') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="w-full max-w-md bg-white rounded-2xl shadow-sm border border-gray-100 p-8 text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-orange-50 flex items-center justify-center">
            <i className="ri-error-warning-line text-3xl text-orange-500" />
          </div>
          <h2 className="text-xl font-semibold text-gray-800 mb-2">站点已关闭</h2>
          <p className="text-sm text-gray-500 mb-6 leading-relaxed">
            {siteName ? `「${siteName}」` : '该站点'}已被管理员停止或已到期，暂时无法访问。
            <br />如需恢复，请联系站点管理员处理。
          </p>
          <div className="p-4 bg-gray-50 rounded-xl text-xs text-gray-400">
            如有疑问请联系您的代理商或平台客服
          </div>
        </div>
      </div>
    );
  }

  if (state === 'unknown') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="w-full max-w-md bg-white rounded-2xl shadow-sm border border-gray-100 p-8 text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-50 flex items-center justify-center">
            <i className="ri-global-line text-3xl text-gray-400" />
          </div>
          <h2 className="text-xl font-semibold text-gray-800 mb-2">站点不存在</h2>
          <p className="text-sm text-gray-500 mb-6 leading-relaxed">
            未找到当前域名对应的站点，请检查域名是否正确。
          </p>
          <div className="p-4 bg-gray-50 rounded-xl text-xs text-gray-400">
            如果您是站点管理员，请确保域名已正确绑定
          </div>
        </div>
      </div>
    );
  }

  return <Home />;
}

/** 根路径域名感知组件：主域名跳转后台登录页，子域名通过守卫检查后显示首页 */
function DomainAwareRoot() {
  if (isMainDomain()) {
    return <Navigate to="/admin/login" replace />;
  }
  return <SubdomainSiteGuard />;
}

/** 登录页域名感知组件：主域名禁止访问前台登录，跳转后台登录页。子域名按模板动态加载登录页 */
function DomainAwareLogin() {
  const LoginTemplate = useLoginTemplate();
  return isMainDomain() ? <Navigate to="/admin/login" replace /> : <LoginTemplate />;
}

/** 注册页模板感知组件：按模板动态加载注册页 */
function TemplateAwareRegister() {
  const RegisterTemplate = useRegisterTemplate();
  return <RegisterTemplate />;
}

/** VIP页模板感知组件：按模板动态加载VIP页 */
function TemplateAwareVip() {
  const VipTemplate = useVipTemplate();
  return <VipTemplate />;
}

const routes: RouteObject[] = [
  {
    path: "/",
    element: <DomainAwareRoot />,
  },
  { path: "/category", element: <CategoryPage /> },
  { path: "/category/:slug", element: <CategoryPage /> },
  { path: "/listing/:id", element: <ListingDetail /> },
  { path: "/search", element: <SearchPage /> },
  { path: "/login", element: <DomainAwareLogin /> },
  { path: "/register", element: <TemplateAwareRegister /> },
  { path: "/forgot-password", element: <ForgotPasswordPage /> },
  { path: "/vip", element: <TemplateAwareVip /> },
  { path: "/admin/login", element: <AdminLoginPage /> },
  {
    path: "/publish",
    element: <RequireAuth><PublishPage /></RequireAuth>,
  },
  {
    path: "/dashboard",
    element: <RequireAuth><DashboardPage /></RequireAuth>,
  },
  {
    path: "/dashboard/profile",
    element: <RequireAuth><ProfilePage /></RequireAuth>,
  },
  {
    path: "/dashboard/my-listings",
    element: <RequireAuth><MyListingsPage /></RequireAuth>,
  },
  {
    path: "/dashboard/edit-listing/:id",
    element: <RequireAuth><EditListingPage /></RequireAuth>,
  },
  {
    path: "/dashboard/favorites",
    element: <RequireAuth><FavoritesPage /></RequireAuth>,
  },
  {
    path: "/dashboard/history",
    element: <RequireAuth><HistoryPage /></RequireAuth>,
  },
  {
    path: "/dashboard/payment-records",
    element: <RequireAuth><PaymentRecordsPage /></RequireAuth>,
  },
  {
    path: "/admin",
    element: <RequireAdmin><AdminDashboard /></RequireAdmin>,
  },
  {
    path: "/admin/listings",
    element: <RequireAdmin><AdminListingsPage /></RequireAdmin>,
  },
  {
    path: "/admin/users",
    element: <RequireAdmin><AdminUsersPage /></RequireAdmin>,
  },
  {
    path: "/admin/reports",
    element: <RequireAdmin><AdminReportsPage /></RequireAdmin>,
  },
  {
    path: "/admin/vip-orders",
    element: <RequireAdmin><AdminVipOrdersPage /></RequireAdmin>,
  },
  {
    path: "/admin/settings",
    element: <RequireAdmin><AdminSettingsPage /></RequireAdmin>,
  },
  {
    path: "/admin/categories",
    element: <RequireAdmin><AdminCategoriesPage /></RequireAdmin>,
  },
  {
    path: "/admin/payment",
    element: <RequireAdmin><AdminPaymentPage /></RequireAdmin>,
  },
  {
    path: "/admin/publish",
    element: <RequireAdmin><AdminPublishPage /></RequireAdmin>,
  },
  {
    path: "/admin/edit-listing/:id",
    element: <RequireAdmin><AdminEditListingPage /></RequireAdmin>,
  },
  {
    path: "/admin/nav-menus",
    element: <RequireAdmin><AdminNavMenusPage /></RequireAdmin>,
  },
  {
    path: "/admin/single-pages",
    element: <RequireAdmin><AdminSinglePagesPage /></RequireAdmin>,
  },
  {
    path: "/admin/pin-management",
    element: <RequireAdmin><AdminPinManagementPage /></RequireAdmin>,
  },
  {
    path: "/admin/decoration",
    element: <RequireAdmin><AdminDecorationPage /></RequireAdmin>,
  },
  {
    path: "/admin/cloud-data",
    element: <RequireAdmin><AdminCloudDataPage /></RequireAdmin>,
  },
  {
    path: "/admin/my-sites",
    element: <RequireAdmin><AdminMySitesPage /></RequireAdmin>,
  },
  {
    path: "/tenant",
    element: <RequireAdmin><TenantIndexPage /></RequireAdmin>,
  },
  {
    path: "/tenant/sites",
    element: <RequireAdmin><TenantSitesPage /></RequireAdmin>,
  },
  {
    path: "/tenant/account",
    element: <RequireAdmin><TenantAccountPage /></RequireAdmin>,
  },
  {
    path: "/tenant/dashboard",
    element: <RequireAdmin><TenantDashboardPage /></RequireAdmin>,
  },
  {
    path: "/tenant/users",
    element: <RequireAdmin><TenantUsersPage /></RequireAdmin>,
  },
  {
    path: "/tenant/plans",
    element: <RequireAdmin><TenantPlansPage /></RequireAdmin>,
  },
  {
    path: "/platform",
    element: <RequireAdmin><PlatformIndexPage /></RequireAdmin>,
  },
  {
    path: "/platform/dashboard",
    element: <RequireAdmin><PlatformDashboardPage /></RequireAdmin>,
  },
  {
    path: "/platform/agents",
    element: <RequireAdmin><PlatformAgentsPage /></RequireAdmin>,
  },
  {
    path: "/platform/plans",
    element: <RequireAdmin><PlatformPlansPage /></RequireAdmin>,
  },
  {
    path: "/platform/sites",
    element: <RequireAdmin><PlatformSitesPage /></RequireAdmin>,
  },
  {
    path: "/platform/settings",
    element: <RequireAdmin><PlatformSettingsPage /></RequireAdmin>,
  },
  { path: "/page/:slug", element: <SinglePageView /> },
  { path: "*", element: <NotFound /> },
];

export default routes;
